import dayjs from 'dayjs';

import { ValidateInfo } from './types';
import { ParamItem } from '@/api/services/parameterSchemaService';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { SettleScope } from './settleContext';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export function formatNum(val: string) {
  const num = parseFloat(val);
  return isNaN(num) ? undefined : num;
}

export function isNullLike(val: any): val is undefined | null {
  return val === undefined || val === null;
}

export function isEmpty(val: any): val is undefined | null {
  return val === undefined || val === null || val === '';
}

export function emptyValidator(val: any, enableEmpty?: boolean) {
  return !enableEmpty &&
    (isNullLike(val) ||
      (val instanceof Array && !val.length) ||
      (typeof val === 'string' && !val))
    ? 'Should not be empty'
    : '';
}

export function numRangeValidator(
  val: { min?: string; max?: string },
  enableEmpty?: boolean,
) {
  let error = emptyValidator(val?.min, enableEmpty);
  if (error) {
    // return { min: error }
    return `Min: ${error}`;
  }
  error = emptyValidator(val?.max, enableEmpty);
  if (error) {
    // return { max: error }
    return `Max: ${error}`;
  }
  return +val.min! > +val.max! ? 'Max should bigger than Min' : '';
}

export function expiryValidator(val: string, enableEmpty?: boolean) {
  const error = emptyValidator(val, enableEmpty);
  if (error) return error;
  // const $val = dayjs(val.replace(/([a-zA-Z]+)/, ' $1 '), 'D MMM YY')
  //   .format('DMMMYY')
  //   .toUpperCase();
  // if ($val !== val && `0${$val}` !== val) return 'Out of format';
  return '';
}

export function expiriesValidator(val: string[]) {
  if (!val?.length) return '';
  for (let i = 0; i < val.length; i++) {
    const error = emptyValidator(val[i]);
    if (error) {
      // return { [i]: error }
      return `The ${i + 1}${
        i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
      } item: ${error}`;
    }
  }
  return '';
}

export function keyValidator(val: string, allKeys: string[]) {
  const error = emptyValidator(val);
  if (error) return error;
  if (!/^[_.a-zA-Z0-9]+$/.test(val)) {
    return 'Out of format: should be a combination of the following chars `_.0-9a-zA-Z`';
  }
  const has = allKeys.some(it => val === it);
  if (has) return 'This key has been used';
  return '';
}

/** 结算规则：时区 + 当日结算时刻 */
interface SettleRule {
  zone: string;
  time: string;
}

const UTC_08: SettleRule = { zone: 'UTC', time: '08:00' };
const NY_16: SettleRule = { zone: 'America/New_York', time: '16:00' };
const NY_17: SettleRule = { zone: 'America/New_York', time: '17:00' };

/**
 * 与后端 common.domain.SettleTime 保持一致的结算规则：
 * - Binance XAU：纽约 17:00
 * - Bybit TradFi：跟随美股收盘，纽约 16:00
 * - 其余：UTC 08:00
 * 上下文不完整时返回多条候选规则，调用方取最晚的一条（保守：宁可多显示，不提前隐藏）。
 */
export function settleRulesOf(scope?: SettleScope): SettleRule[] {
  const exchange = scope?.exchange?.toUpperCase();
  const currency = scope?.currency?.toUpperCase();
  const usStock = ['NVDA', 'SPCX', 'SNDK', 'SKHY', 'MU', 'TSLA', 'QQQ', 'SOXL'].includes(currency ?? '');
  if (exchange && currency) {
    if (exchange === 'BINANCE' && currency === 'XAU') return [NY_17];
    if (exchange === 'BYBIT' && usStock) return [NY_16];
    return [UTC_08];
  }
  if (currency) {
    if (currency === 'XAU') return [NY_17];
    if (usStock) return [NY_16];
    return [UTC_08];
  }
  return [UTC_08, NY_16, NY_17];
}

/**
 * 判断 expiry（如 14SEP26）是否已到期。
 * 不传 scope 时按最晚的结算规则判断，只会"晚隐藏"，不会把仍有效的条目提前滤掉。
 */
export function isExpired(val?: string, scope?: SettleScope) {
  if (!val) return false;
  const expiry = dayjs(
    val
      .match(/\d+|[a-zA-Z]+/g)
      ?.slice(0, 3)
      .join('-') || '',
    'D-MMM-YY',
  );
  if (!expiry.isValid()) return false;
  const day = expiry.format('YYYY-MM-DD');
  const latestSettle = Math.max(
    ...settleRulesOf(scope).map(rule => dayjs.tz(`${day} ${rule.time}`, 'YYYY-MM-DD HH:mm', rule.zone).valueOf()),
  );
  return dayjs().valueOf() >= latestSettle;
}

export function sameGroupNameValidator(
  val: string,
  paramList: ParamItem[],
  projectName: string,
  templateName: string,
  parentNames?: string,
) {
  const error = emptyValidator(val);
  if (error) return error;
  const hasSame = paramList?.some(_it => _it.name === val);
  return hasSame
    ? `该参数组名称在${projectName}>${templateName}${
        parentNames ? '>' + parentNames : ''
      }下已存在`
    : '';
}

export function validatorCvt(
  validator: (val: any, values: any) => ValidateInfo['string'],
  form?: () => any,
) {
  return (_: any, val: any) => {
    const $form = form?.();
    const error = validator(val, $form?.getFieldsValue());
    if (!error) return Promise.resolve();
    const traverse = (obj: ValidateInfo['string']): void | Promise<any> => {
      if (typeof obj === 'string' && obj) return Promise.reject(new Error(obj));
      if (obj && typeof obj === 'object') {
        for (const key in obj) {
          const res = traverse(obj[key]);
          if (res) return res;
        }
      }
    };
    return traverse(error) || Promise.resolve();
  };
}
